import { Component, Inject, OnInit, signal } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Daypart, DaypartDay, Exception, User } from 'src/app/interfaces';
import moment from 'moment-timezone';
import { getDateString, valueToDate } from 'src/app/globals';
import {
  deleteDoc,
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { dataStore, db } from 'src/app/app.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatChipListbox, MatChip } from '@angular/material/chips';
import { MatTooltip } from '@angular/material/tooltip';
import {
  CopyDateSequenceComponent,
  CopyDateSequenceStatus,
  CopyDateSequenceWarning,
} from '../copy-date-sequence/copy-date-sequence.component';

type UserException = NonNullable<User['exceptions']>[number];

@Component({
  selector: 'app-copy-daypart',
  templateUrl: './copy-daypart.component.html',
  styleUrls: ['./copy-daypart.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatError,
    MatInput,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatChipListbox,
    MatChip,
    MatTooltip,
  ],
})
export class CopyDaypartComponent implements OnInit {
  copyForm: UntypedFormGroup;
  dayPartData: Daypart;

  customMessage = signal<string>('');
  customMessageList = signal<any>([]);
  driverId: string;
  plannerIds = signal<string[]>([]);
  rebuildingSequence = signal(false);
  copyInProgress = signal(false);
  dateSequence = signal<string[]>([]);
  warnings = signal<Record<string, CopyDateSequenceWarning>>({});
  private sequenceBuildId = 0;

  organisationId = localStorage.getItem('orgId');

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: UntypedFormBuilder,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<CopyDaypartComponent>,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    console.log('CopyDaypartComponent data:', this.data);
    this.copyForm = this.fb.group({
      copyType: ['weekly', Validators.required],
      copyTimes: [1, Validators.required],
    });
    this.dayPartData = this.data.daypartData.dayPart;
    if (this.data.type == 'driver') {
      const vehicle = this.data.daypartData?.vehicle;
      const privateVehicle = vehicle?.vehicleType === 'private';
      this.driverId = privateVehicle ? (vehicle.driverId ?? '') : this.data.driverId;
      if (privateVehicle) {
        this.data.coDriverId = dataStore.generalSettings.coDriverEnabled
          ? (vehicle.coDriverId ?? 'none')
          : 'none';
      }
      if (this.driverId === '') {
        this.customMessage.set(
          'Er is geen chauffeur geselecteerd. Alleen bestaande diensten worden aangepast. Er worden geen nieuwe diensten aangemaakt.',
        );
      }
    } else if (this.data.type == 'planner') {
      this.plannerIds.set(this.data.plannerIds);
      if (this.plannerIds().length == 0) {
        this.customMessage.set(
          'Er is geen planner geselecteerd. Alleen bestaande diensten worden aangepast. Er worden geen nieuwe diensten aangemaakt.',
        );
      }
    }
    this.copyForm.valueChanges.subscribe(() => this.buildDateSequence());
    this.buildDateSequence();
  }

  getWeekString(date) {
    const weekYear = moment(date).isoWeekYear();
    const week = moment(date).isoWeek();
    return `${weekYear}-${week}`;
  }

  async getTargetStatus(
    path: string,
    end: Date,
    hasAssignment: boolean,
  ): Promise<CopyDateSequenceStatus | null> {
    const target = await getDoc(doc(db, path));
    const targetEndDate = valueToDate(target.data()?.end);
    const targetIsFinished =
      moment(end).isSameOrBefore(moment()) ||
      (!!targetEndDate && moment(targetEndDate).isSameOrBefore(moment()));

    if (targetIsFinished) return 'finished';
    if (!target.exists()) return hasAssignment ? 'created' : null;
    return hasAssignment ? 'updated' : 'removed';
  }

  getStatusMessage(status: CopyDateSequenceStatus): string {
    switch (status) {
      case 'updated':
        return 'De gegevens van deze dienst worden bijgewerkt.';
      case 'finished':
        return 'Deze dienst is al voorbij en blijft ongewijzigd.';
      case 'removed':
        return `Deze dienst wordt verwijderd, omdat er geen ${this.data.type === 'driver' ? 'chauffeur' : 'planner'} is geselecteerd.`;
      default:
        return '';
    }
  }

  hasNoViableCopyDates(): boolean {
    const dates = this.dateSequence();
    if (dates.length === 0) return false;

    return dates.every((dateString) => {
      const warning = this.warnings()[dateString];
      if (!warning) return false;
      if (warning.status === 'finished') return true;
      if (this.data.type === 'planner') return warning.allPlannersUnavailable === true;
      if (warning.exception) return true;
      return warning.blockingDoubleBooking === true;
    });
  }

  async getPreviewWarnings(
    start: Date,
    end: Date,
    targetPath: string,
    hasAssignment: boolean,
  ): Promise<CopyDateSequenceWarning> {
    const status = await this.getTargetStatus(targetPath, end, hasAssignment);
    const warning: CopyDateSequenceWarning =
      status && status !== 'created' ? { status, message: this.getStatusMessage(status) } : {};
    if (!hasAssignment || status === 'finished') return warning;

    const warningMessages = warning.message ? [warning.message] : [];
    const addWarning = (key: 'doubleBooking' | 'exception', message: string) => {
      warning[key] = true;
      warningMessages.push(message);
      warning.message = warningMessages.join('\n');
    };
    const checkUser = async (userId: string, docId: string, name: string, blocksCopy: boolean) => {
      const user = (
        await getDoc(doc(db, `organisations/${this.organisationId}/users/${userId}`))
      ).data() as User | undefined;
      if (
        user?.exceptions?.some(
          (exception: UserException) =>
            moment(start).isBefore(moment(valueToDate(exception.dateTo))) &&
            moment(end).isAfter(moment(valueToDate(exception.dateFrom))),
        )
      ) {
        addWarning('exception', `${name} is niet beschikbaar.`);
      } else if (dataStore.generalSettings?.doubleBookingActivated === false) {
        const bookingRoles = await this.getEmployeeBookingRoles(userId, start, end, docId);
        if (bookingRoles.length > 0) {
          const crossTypeRoles = bookingRoles.filter((role) => role === 'planner');
          addWarning(
            'doubleBooking',
            crossTypeRoles.length > 0
              ? `${name} is op dit tijdstip al ingepland als ${crossTypeRoles.join(' en ')} voor een andere dienst.`
              : `${name} is op dit tijdstip al ingepland voor een andere dienst.`,
          );
          if (blocksCopy) warning.blockingDoubleBooking = true;
        }
      }
    };

    if (this.data.type === 'driver' && this.driverId && this.driverId !== 'none') {
      const docId = `${getDateString(start)}.${this.dayPartData.id}.${this.data.vehicleId}`;
      await checkUser(this.driverId, docId, 'De chauffeur', true);
      if (this.data.coDriverId && this.data.coDriverId !== 'none') {
        await checkUser(this.data.coDriverId, docId, 'De bijrijder', true);
      }
    } else if (this.data.type === 'planner') {
      const docId = `${getDateString(start)}.${this.dayPartData.id}`;
      let unavailablePlannerCount = 0;
      let doubleBookedPlannerCount = 0;
      const doubleBookingRoleCounts = new Map<string, number>();
      for (const plannerId of this.plannerIds()) {
        const user = (
          await getDoc(doc(db, `organisations/${this.organisationId}/users/${plannerId}`))
        ).data() as User | undefined;
        const unavailable = user?.exceptions?.some(
          (exception: UserException) =>
            moment(start).isBefore(moment(valueToDate(exception.dateTo))) &&
            moment(end).isAfter(moment(valueToDate(exception.dateFrom))),
        );
        if (unavailable) {
          unavailablePlannerCount++;
        } else if (dataStore.generalSettings?.doubleBookingActivated === false) {
          const bookingRoles = await this.getEmployeeBookingRoles(plannerId, start, end, docId);
          for (const role of bookingRoles) {
            if (role === 'planner') {
              doubleBookedPlannerCount++;
            } else {
              doubleBookingRoleCounts.set(role, (doubleBookingRoleCounts.get(role) ?? 0) + 1);
            }
          }
        }
      }
      if (doubleBookedPlannerCount > 0) {
        addWarning(
          'doubleBooking',
          `${doubleBookedPlannerCount} ${doubleBookedPlannerCount === 1 ? 'planner is' : 'planners zijn'} al ingepland voor een andere dienst.`,
        );
      }
      for (const [role, count] of doubleBookingRoleCounts) {
        addWarning('doubleBooking', this.getDoubleBookingMessage(count, role, 'planner'));
      }
      if (unavailablePlannerCount > 0) {
        addWarning(
          'exception',
          `${unavailablePlannerCount} ${unavailablePlannerCount === 1 ? 'planner is' : 'planners zijn'} niet beschikbaar.`,
        );
        if (unavailablePlannerCount === this.plannerIds().length) {
          warning.allPlannersUnavailable = true;
        }
      }
    }
    return warning;
  }

  async buildDateSequence(): Promise<void> {
    const buildId = ++this.sequenceBuildId;
    const copyTimes = Number(this.copyForm.get('copyTimes')?.value || 0);
    if (!copyTimes || !this.copyForm.valid) {
      this.dateSequence.set([]);
      this.warnings.set({});
      this.rebuildingSequence.set(false);
      return;
    }

    this.rebuildingSequence.set(true);
    try {
      const copyValue = this.copyForm.get('copyType')?.value === 'weekly' ? 7 : 14;
      const dateSequence: string[] = [];
      const warnings: Record<string, CopyDateSequenceWarning> = {};
      const hasAssignment =
        this.data.type === 'driver'
          ? !!this.driverId && this.driverId !== 'none'
          : this.plannerIds().length > 0 && this.data.planners.length > 0;
      const vehicleSuffix = this.data.type === 'driver' ? `.${this.data.vehicleId}` : '';

      for (let i = 1; i <= copyTimes; i++) {
        const start = moment
          .tz(valueToDate(this.data.daypartData.start), 'Europe/Amsterdam')
          .add(copyValue * i, 'days');
        const end = moment
          .tz(valueToDate(this.data.daypartData.end), 'Europe/Amsterdam')
          .add(copyValue * i, 'days');
        const startDate = start.toDate();
        const dateString = getDateString(startDate);
        const targetPath = `organisations/${this.organisationId}/daypartDays/${dateString}.${this.dayPartData.id}${vehicleSuffix}`;
        const warning = await this.getPreviewWarnings(
          startDate,
          end.toDate(),
          targetPath,
          hasAssignment,
        );

        if (buildId !== this.sequenceBuildId) return;
        dateSequence.push(dateString);
        if (warning.status || warning.doubleBooking || warning.exception)
          warnings[dateString] = warning;
      }

      if (buildId !== this.sequenceBuildId) return;
      this.dateSequence.set(dateSequence);
      this.warnings.set(warnings);
    } catch {
      if (buildId === this.sequenceBuildId) {
        this.dateSequence.set([]);
        this.warnings.set({});
        this.snackBar.open('De datums konden niet worden gecontroleerd. Probeer opnieuw.', 'X', {
          duration: 8000,
        });
      }
    } finally {
      if (buildId === this.sequenceBuildId) {
        this.rebuildingSequence.set(false);
      }
    }
  }

  viewDateSequence() {
    this.dialog.open(CopyDateSequenceComponent, {
      width: '475px',
      autoFocus: false,
      data: {
        type: 'daypart',
        dateSequence: this.dateSequence(),
        warnings: this.warnings(),
      },
    });
  }

  async copyDaypart() {
    if (this.copyInProgress()) return;
    if (this.copyForm.valid) {
      this.copyInProgress.set(true);
      try {
        const copyType = this.copyForm.value.copyType;
        const copyTimes = this.copyForm.value.copyTimes;

        const copyValue = copyType === 'weekly' ? 7 : 14;
        let promises = [];
        let driverHasException = false;
        let coDriverHasException = false;
        this.customMessageList.set({});
        let copiesQueued = 0;

        for (let i = 1; i <= copyTimes; i++) {
          // start with 1 instead of 0 because of the date calculation

          const saveObj: DaypartDay = {};
          const startDate = moment
            .tz(valueToDate(this.data.daypartData.start), 'Europe/Amsterdam')
            .add(copyValue * i, 'days');
          const endDate = moment
            .tz(valueToDate(this.data.daypartData.end), 'Europe/Amsterdam')
            .add(copyValue * i, 'days');

          saveObj.start = startDate.toDate();
          saveObj.end = endDate.toDate();

          saveObj.type = this.data.type;
          saveObj.date = saveObj.start;
          saveObj.startString = getDateString(saveObj.start);
          saveObj.year = startDate.year();
          saveObj.month = startDate.month();
          saveObj.week = this.getWeekString(saveObj.start);
          saveObj.day = startDate.date();
          saveObj.daypartId = this.dayPartData.id;
          saveObj.lastAlerted = null;
          saveObj.lastUpdated = new Date();
          saveObj.startHour = startDate.hour();
          saveObj.startMinute = startDate.minute();
          saveObj.endHour = endDate.hour();
          saveObj.endMinute = endDate.minute();
          saveObj.comment = this.data.daypartData?.dayPartDay?.comment ?? null;

          if (this.data.type == 'driver') {
            saveObj.vehicleId = this.data.vehicleId;
            saveObj.driverId = this.driverId;

            if (this.driverId && this.driverId !== 'none') {
              const driverData = (
                await getDoc(doc(db, `organisations/${this.organisationId}/users/${this.driverId}`))
              ).data();
              saveObj.driverName = driverData.name;

              // handle exceptions
              let firstException: Exception;
              if (driverData.exceptions) {
                const exceptions = driverData.exceptions.sort((a: Exception, b: Exception) => {
                  return moment(valueToDate(b.dateFrom)).diff(moment(valueToDate(a.dateTo)));
                });
                exceptions.forEach((exception: Exception) => {
                  const exceptionStartDate = moment(valueToDate(exception.dateFrom));
                  const exceptionEndDate = moment(valueToDate(exception.dateTo));

                  if (
                    moment(saveObj.start).isBefore(exceptionEndDate) &&
                    moment(saveObj.end).isAfter(exceptionStartDate)
                  ) {
                    driverHasException = true;
                    if (!firstException) {
                      firstException = exception;
                    }
                    return;
                  }
                });
              }

              if (driverHasException) {
                this.customMessage.set(
                  `De ritten zijn niet volledig gekopieerd omdat de chauffeur niet op alle dagdelen aanwezig is.`,
                );
              }

              // check double booking
              if (
                !driverHasException &&
                dataStore.generalSettings?.doubleBookingActivated === false
              ) {
                const alreadyBooked = await this.checkIfEmployeeAlreadyBooked(
                  this.driverId,
                  saveObj.start,
                  saveObj.end,
                  `${saveObj.startString}.${saveObj.daypartId}.${this.data.vehicleId}`,
                );
                if (alreadyBooked) {
                  driverHasException = true;
                  this.customMessage.set(
                    'De chauffeur is op dit tijdstip al ingepland voor een andere dienst.',
                  );
                }
              }

              if (this.data.coDriverId && this.data.coDriverId !== 'none') {
                const coDriverData = (
                  await getDoc(
                    doc(db, `organisations/${this.organisationId}/users/${this.data.coDriverId}`),
                  )
                ).data();
                // handle exceptions
                coDriverHasException = false;
                if (coDriverData.exceptions) {
                  const exceptions = coDriverData.exceptions.sort((a: Exception, b: Exception) => {
                    return moment(valueToDate(b.dateFrom)).diff(moment(valueToDate(a.dateTo)));
                  });
                  for (const exception of exceptions) {
                    const exceptionStartDate = moment(valueToDate(exception.dateFrom));
                    const exceptionEndDate = moment(valueToDate(exception.dateTo));

                    if (
                      moment(saveObj.start).isBefore(exceptionEndDate) &&
                      moment(saveObj.end).isAfter(exceptionStartDate)
                    ) {
                      coDriverHasException = true;
                      this.customMessage.set(
                        `De ritten zijn niet volledig gekopieerd omdat de chauffeur of bijrijder niet op alle dagdelen aanwezig is.`,
                      );
                      break;
                    }
                  }
                }
                if (!coDriverHasException) {
                  // check double booking for co-driver
                  if (dataStore.generalSettings?.doubleBookingActivated === false) {
                    const alreadyBooked = await this.checkIfEmployeeAlreadyBooked(
                      this.data.coDriverId,
                      saveObj.start,
                      saveObj.end,
                      `${saveObj.startString}.${saveObj.daypartId}.${this.data.vehicleId}`,
                    );
                    if (alreadyBooked) {
                      coDriverHasException = true;
                      this.customMessage.set(
                        'De bijrijder is op dit tijdstip al ingepland voor een andere dienst.',
                      );
                    }
                  }
                  if (!coDriverHasException) {
                    saveObj.coDriverId = this.data.coDriverId;
                    saveObj.coDriverName = coDriverData.name;
                  }
                }
              }
              if (!driverHasException && !coDriverHasException) {
                const targetPath = `organisations/${this.organisationId}/daypartDays/${saveObj.startString}.${saveObj.daypartId}.${this.data.vehicleId}`;
                const targetStatus = await this.getTargetStatus(targetPath, saveObj.end, true);
                if (targetStatus !== 'finished') {
                  promises.push(setDoc(doc(db, targetPath), saveObj, { merge: true }));
                  copiesQueued++;
                }
              }
              driverHasException = false;
            } else {
              // there is no driver selected, remove all
              const targetPath = `organisations/${this.organisationId}/daypartDays/${saveObj.startString}.${saveObj.daypartId}.${this.data.vehicleId}`;
              const targetStatus = await this.getTargetStatus(targetPath, saveObj.end, false);
              if (targetStatus === 'removed') {
                promises.push(deleteDoc(doc(db, targetPath)));
              }
            }
          } else if (this.data.type == 'planner') {
            if (this.plannerIds().length > 0 && this.data.planners.length > 0) {
              saveObj.plannerIds = [...this.plannerIds()];
              saveObj.planners = [...this.data.planners];
              let targetHasDoubleBooking = false;
              for (const plannerId of this.plannerIds()) {
                const plannerData = (
                  await getDoc(
                    doc(db, `organisations/${this.organisationId}/users/`, `${plannerId}`),
                  )
                ).data() as User;

                let hasException = false;
                if (plannerData.exceptions) {
                  const exceptions = plannerData.exceptions.sort(
                    (a: UserException, b: UserException) => {
                      return moment(valueToDate(b.dateFrom)).diff(moment(valueToDate(a.dateTo)));
                    },
                  );
                  exceptions.forEach((exception: UserException) => {
                    const exceptionStartDate = moment(valueToDate(exception.dateFrom));
                    const exceptionEndDate = moment(valueToDate(exception.dateTo));

                    if (
                      moment(saveObj.start).isBefore(exceptionEndDate) &&
                      moment(saveObj.end).isAfter(exceptionStartDate)
                    ) {
                      hasException = true;
                      return;
                    }
                  });
                }

                if (hasException) {
                  const messageList = this.customMessageList();
                  if (!messageList[plannerData.name]) {
                    messageList[plannerData.name] = [];
                  }
                  messageList[plannerData.name].push(saveObj.startString);
                  this.customMessageList.set(messageList);
                  saveObj.plannerIds.splice(saveObj.plannerIds.indexOf(plannerData.id), 1);
                  saveObj.planners.splice(saveObj.planners.indexOf(plannerData.name), 1);
                } else {
                  // check double booking for planner
                  if (dataStore.generalSettings?.doubleBookingActivated === false) {
                    const alreadyBooked = await this.checkIfEmployeeAlreadyBooked(
                      plannerId,
                      saveObj.start,
                      saveObj.end,
                      `${saveObj.startString}.${saveObj.daypartId}`,
                    );
                    if (alreadyBooked) {
                      targetHasDoubleBooking = true;
                      this.customMessage.set(
                        'Een planner is op dit tijdstip al ingepland voor een andere dienst.',
                      );
                      const plannerIndex = saveObj.plannerIds.indexOf(plannerId);
                      const plannerNameIndex = saveObj.planners.indexOf(plannerData.name);
                      if (plannerIndex !== -1) {
                        saveObj.plannerIds.splice(plannerIndex, 1);
                      }
                      if (plannerNameIndex !== -1) {
                        saveObj.planners.splice(plannerNameIndex, 1);
                      }
                    }
                  }
                }
              }

              const targetPath = `organisations/${this.organisationId}/daypartDays/${saveObj.startString}.${saveObj.daypartId}`;
              const targetStatus = await this.getTargetStatus(targetPath, saveObj.end, true);
              if (targetStatus !== 'finished') {
                if (saveObj.plannerIds.length > 0 && saveObj.planners.length > 0) {
                  promises.push(setDoc(doc(db, targetPath), saveObj, { merge: true }));
                  copiesQueued++;
                } else if (!targetHasDoubleBooking) {
                  promises.push(deleteDoc(doc(db, targetPath)));
                }
              }
            } else {
              // there is no planner selected, remove all
              const targetPath = `organisations/${this.organisationId}/daypartDays/${saveObj.startString}.${saveObj.daypartId}`;
              const targetStatus = await this.getTargetStatus(targetPath, saveObj.end, false);
              if (targetStatus === 'removed') {
                promises.push(deleteDoc(doc(db, targetPath)));
              }
            }
          }
          if (promises.length > 100) {
            const results = await Promise.allSettled(promises);
            if (results.some((result) => result.status === 'rejected')) {
              throw new Error('One or more daypart copies failed.');
            }
            promises = [];
          }
        }

        await Promise.all(promises);
        if (this.data.type == 'driver') {
          if (this.driverId && this.driverId !== 'none') {
            if (copiesQueued === 0) {
              this.snackBar.open('Er zijn geen kopieën verwerkt.', 'X', {
                duration: 10000,
              });
            } else {
              this.snackBar.open(
                copiesQueued < copyTimes
                  ? 'De kopieën zijn gedeeltelijk verwerkt.'
                  : 'De kopieën zijn verwerkt.',
                'X',
                { duration: 8000 },
              );
              this.dialogRef.close();
            }
          } else {
            this.snackBar.open('Kopiëen succesvol verwijderd.', 'X', {
              duration: 5000,
            });
            this.dialogRef.close();
          }
        } else if (this.data.type == 'planner') {
          if (this.plannerIds().length > 0 && this.data.planners.length > 0) {
            if (copiesQueued === 0) {
              this.snackBar.open('Er zijn geen kopieën verwerkt.', 'X', {
                duration: 10000,
              });
            } else {
              this.snackBar.open(
                copiesQueued < copyTimes
                  ? 'De kopieën zijn gedeeltelijk verwerkt.'
                  : 'De kopieën zijn verwerkt.',
                'X',
                { duration: 8000 },
              );
              this.dialogRef.close();
            }
          } else {
            this.snackBar.open('De kopieën zijn verwijderd.', 'X', {
              duration: 5000,
            });
            this.dialogRef.close();
          }
        }
      } catch {
        this.snackBar.open(
          'Het kopiëren is mislukt. Controleer de planning en probeer opnieuw.',
          'X',
          {
            duration: 10000,
          },
        );
      } finally {
        this.copyInProgress.set(false);
      }
    }
  }

  async checkIfEmployeeAlreadyBooked(
    userId: string,
    startDate: Date,
    endDate: Date,
    docId: string,
  ) {
    const bookingRoles = await this.getEmployeeBookingRoles(userId, startDate, endDate, docId);
    return bookingRoles.length > 0;
  }

  private async getEmployeeBookingRoles(
    userId: string,
    startDate: Date,
    endDate: Date,
    docId: string,
  ): Promise<string[]> {
    const start = moment(startDate);
    const end = moment(endDate);

    const baseQuery = collection(db, `organisations/${this.organisationId}/daypartDays`);
    const dateFilters = [
      where('day', '==', start.date()),
      where('month', '==', start.month()),
      where('year', '==', start.year()),
    ];

    const queries = [
      query(baseQuery, ...dateFilters, where('driverId', '==', userId)),
      query(baseQuery, ...dateFilters, where('coDriverId', '==', userId)),
      query(baseQuery, ...dateFilters, where('plannerIds', 'array-contains', userId)),
    ];

    const results = await Promise.all(queries.map((q) => getDocs(q)));
    const allDocs = results.flatMap((snapshot) => snapshot.docs);

    const bookingRoles = new Set<string>();
    for (const daypartDayDoc of allDocs) {
      if (daypartDayDoc.id === docId) continue;

      const daypartDay = daypartDayDoc.data() as DaypartDay;
      const existingStart = moment(valueToDate(daypartDay.start));
      const existingEnd = moment(valueToDate(daypartDay.end));

      if (start.isBefore(existingEnd) && existingStart.isBefore(end)) {
        if (daypartDay.driverId === userId) bookingRoles.add('chauffeur');
        if (daypartDay.coDriverId === userId) bookingRoles.add('bijrijder');
        if (daypartDay.plannerIds?.includes(userId)) bookingRoles.add('planner');
      }
    }
    return [...bookingRoles];
  }

  private getDoubleBookingMessage(count: number, role: string, targetRole: string): string {
    const targetLabels: Record<string, [string, string]> = {
      planner: ['planner', 'planners'],
    };
    const [singular, plural] = targetLabels[targetRole] ?? [targetRole, `${targetRole}s`];
    return `${count} ${count === 1 ? singular : plural} ${count === 1 ? 'is' : 'zijn'} al ingepland als ${role} voor een andere dienst.`;
  }

  async closeDialog() {
    this.dialogRef.close();
  }
}
